import { CubeBuilder, Place } from 'wdc-cube';
import { AccountPresenter } from './concrete/account/account.presenter';
import { LandingPresenter } from './concrete/landing/landing.presenter';
import { SignInPresenter } from './concrete/account/sign-in/auth.presenter';
import { AppDeleteAccountPresenter } from './concrete/account/delete/app-delete-account.presenter';
import { ForgotPasswordPresenter } from './concrete/account/forgot/forgot-password.presenter';
import { SharedHistoryPresenter } from './concrete/shared-history/shared-history.presenter';
import { CollaboratorDetailPresenter } from './concrete/restricted/collaborators/detail/collaborator-detail.presenter';
import { MonitoringDetailPresenter } from './concrete/restricted/monitoring/detail/monitoring-detail.presenter';
import { CollaboratorsPresenter } from './concrete/restricted/collaborators/collaborators.presenter';
import { CollaboratorListPresenter } from './concrete/restricted/collaborators/list/collaborator-list.presenter';
import { CompaniesPresenter } from './concrete/restricted/companies/companies.presenter';
import { CompanyConfirmPresenter } from './concrete/restricted/companies/confirm/company-confirm.presenter';
import { CompanyEditPresenter } from './concrete/restricted/companies/edit/company-edit.presenter';
import { CompanyUsersPresenter } from './concrete/restricted/company-users/company-users.presenter';
import { CompanyUserDetailPresenter } from './concrete/restricted/company-users/detail/company-user-detail.presenter';
import { CompanyUserFormPresenter } from './concrete/restricted/company-users/form/company-user-form.presenter';
import { DashboardPresenter } from './concrete/restricted/dashboard/dashboard.presenter';
import { MonitoringPresenter } from './concrete/restricted/monitoring/monitoring.presenter';
import { MonitoringListPresenter } from './concrete/restricted/monitoring/list/monitoring-list.presenter';
import { MyDataPresenter } from './concrete/restricted/my-data/my-data.presenter';
import { SleepReportPresenter } from './concrete/restricted/sleep-report/sleep-report.presenter';
import { UserBasePresenter } from './concrete/restricted/user-base/user-base.presenter';
import { IcpPresenter } from './concrete/restricted/icp/icp.presenter';
import { RestrictedPresenter } from './concrete/restricted/restricted.presenter';
import { Places } from './route-consts';
/**
 * A arvore de places, que tambem e a URL.
 *
 * O aninhamento diz dentro do que um place esta, nao como se chegou nele. Tudo o
 * que exige sessao esta dentro de `hub`, e e por isso que a sessao e verificada
 * uma vez so. `sign-in` fica de fora, porque a porta nao esta atras dela mesma.
 *
 * O vocabulario e o do produto: o cliente chama o painel de HUB, e o endereco
 * passa a dizer o mesmo - `#/hub/dashboard`.
 */
const prepare = CubeBuilder.lazyBuild({
    landing: {
        presenter: Place.creator(LandingPresenter, Places, 'landing')
    },
    /*
     * O CONCEITO "conta", com as quatro telas dele dentro.
     *
     * Entrar, criar, recuperar a senha e apagar a conta do app sao faces do mesmo
     * assunto, e as tres ultimas so se alcancam de dentro da primeira ou por link
     * externo. Antes eram irmas na raiz, e nada na arvore dizia que tinham relacao.
     *
     * `account` nao desenha: e passagem, e quem para nele cai no login. Nao ha
     * guarda - a conta e justamente o que se alcanca SEM sessao.
     */
    account: {
        presenter: Place.creator(AccountPresenter, Places, 'account'),
        'sign-in': {
            presenter: Place.creator(SignInPresenter, Places, 'signIn')
        },
        'forgot-password': {
            presenter: Place.creator(ForgotPasswordPresenter, Places, 'forgotPassword')
        },
        delete: {
            presenter: Place.creator(AppDeleteAccountPresenter, Places, 'appDeleteAccount')
        }
    },
    // Fora do `hub` de proposito: e uma tela PUBLICA, alcancada por um link que o
    // paciente compartilhou. Estar fora da guarda e o que a torna publica - nao ha
    // excecao escrita em lugar nenhum.
    'shared-history': {
        presenter: Place.creator(SharedHistoryPresenter, Places, 'sharedHistory')
    },
    hub: {
        presenter: Place.creator(RestrictedPresenter, Places, 'hub'),
        dashboard: {
            presenter: Place.creator(DashboardPresenter, Places, 'dashboard')
        },
        'user-base': {
            presenter: Place.creator(UserBasePresenter, Places, 'userBase')
        },
        'sleep-report': {
            presenter: Place.creator(SleepReportPresenter, Places, 'sleepReport')
        },
        // O Perfil de Cliente Ideal: so o administrador geral - a guarda e o `allowsSection`.
        icp: {
            presenter: Place.creator(IcpPresenter, Places, 'icp')
        },
        'my-data': {
            presenter: Place.creator(MyDataPresenter, Places, 'myData')
        },
        /*
         * Quem faz parte da empresa: os usuarios que ela administra e a fila de
         * quem pediu para entrar.
         *
         * Uma area so, com duas abas, e nao duas telas. Sao a mesma pergunta em
         * dois momentos - quem entra, e quem ja esta -, e separa-las obrigaria
         * quem aprova um vinculo a procurar a outra tela para conferir o
         * resultado.
         */
        'company-users': {
            presenter: Place.creator(CompanyUsersPresenter, Places, 'companyUsers'),
            /*
             * O CADASTRO e dialogo - ele e desenhado por cima da lista, que continua
             * atras; por isso e filho, e o F5 o mantem aberto.
             *
             * A FICHA e tela: sao quatro secoes e uma lista que rola, e a listagem sai
             * de cena enquanto ela esta aberta. Ela substituiu os dois dialogos que
             * havia antes - corrigir CPF e atribuir colaboradores -, que eram duas
             * portas para o mesmo assunto mostrando um pedaco cada.
             */
            form: {
                presenter: Place.creator(CompanyUserFormPresenter, Places, 'companyUserForm')
            },
            detail: {
                presenter: Place.creator(CompanyUserDetailPresenter, Places, 'companyUserDetail')
            }
        },
        monitoring: {
            presenter: Place.creator(MonitoringPresenter, Places, 'monitoring'),
            // O MESMO detalhe que `collaborators` hospeda, montado tambem aqui - um
            // apresentador generico em dois pontos da arvore. Ver
            // `patient-detail.presenter.ts`: cada montagem tem place e instancia
            // proprios, e o Voltar de cada uma cai na lista que a hospeda, com os
            // filtros dela ainda vivos.
            list: {
                presenter: Place.creator(MonitoringListPresenter, Places, 'monitoringList')
            },
            detail: {
                presenter: Place.creator(MonitoringDetailPresenter, Places, 'monitoringDetail')
            }
        },
        companies: {
            presenter: Place.creator(CompaniesPresenter, Places, 'companies'),
            // Dois dialogos, e dois places. Um dialogo aberto E um lugar onde o
            // leitor esta: o endereco o carrega, o reload o mantem e o Voltar o
            // fecha - sem ninguem escrever um handler de Escape.
            edit: {
                presenter: Place.creator(CompanyEditPresenter, Places, 'companyEdit')
            },
            confirm: {
                presenter: Place.creator(CompanyConfirmPresenter, Places, 'companyConfirm')
            }
        },
        /*
         * O CONCEITO, com as duas telas dele dentro - irmas, e nao aninhadas.
         *
         * `collaborators` nao desenha: e passagem, guarda o filtro comum e
         * reencaminha quem para nele para `list`. O detalhe deixou de ser filho da
         * listagem, entao abri-lo nao mantem mais viva uma tela que nao aparece.
         * Ver `ConceptScope`.
         */
        collaborators: {
            presenter: Place.creator(CollaboratorsPresenter, Places, 'collaborators'),
            list: {
                presenter: Place.creator(CollaboratorListPresenter, Places, 'collaboratorList')
            },
            detail: {
                presenter: Place.creator(CollaboratorDetailPresenter, Places, 'collaboratorDetail')
            }
        }
    }
});
export function initializeRoutes() {
    prepare();
    return Places;
}
//# sourceMappingURL=routes.js.map